import { Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  IconButton,
  SegmentedControl,
  Select,
  Text,
  TextField,
} from "@radix-ui/themes";
import { Trash2, Plus } from "lucide-react";
import { fieldService } from "@/services/breeze/FieldService";
import type { FilterExpression, FilterOp } from "@/types/breeze/Workflow";

interface FilterBuilderProps {
  value: FilterExpression | undefined | null;
  onChange: (next: FilterExpression | undefined) => void;
  entityName: string;
}

type Mode = "and" | "or";

const withMode = (mode: Mode, children: FilterExpression[]): FilterExpression =>
  mode === "or" ? { or: children } : { and: children };

const OPS: { value: FilterOp; label: string; hasValue: boolean }[] = [
  { value: "eq", label: "=", hasValue: true },
  { value: "neq", label: "≠", hasValue: true },
  { value: "is_null", label: "is null", hasValue: false },
  { value: "is_not_null", label: "is not null", hasValue: false },
  { value: "gt", label: ">", hasValue: true },
  { value: "gte", label: "≥", hasValue: true },
  { value: "lt", label: "<", hasValue: true },
  { value: "lte", label: "≤", hasValue: true },
  { value: "in", label: "in (csv)", hasValue: true },
  { value: "contains", label: "contains", hasValue: true },
  { value: "changed", label: "changed", hasValue: false },
  { value: "changed_to", label: "changed to", hasValue: true },
];

const DROPDOWN_Z = { zIndex: 999999 } as const;

function isComposite(expr: FilterExpression): boolean {
  return !!(expr.and || expr.or || expr.not);
}

function emptyLeaf(): FilterExpression {
  return { field: "", op: "eq", value: "" };
}

export default function FilterBuilder({ value, onChange, entityName }: FilterBuilderProps) {
  if (!value) {
    return (
      <Button
        type="button"
        variant="soft"
        onClick={() => onChange({ and: [emptyLeaf()] })}
        disabled={!entityName}
      >
        <Plus size={14} /> Add filter
      </Button>
    );
  }

  return (
    <Flex direction="column" gap="2">
      <Flex align="center" justify="between">
        <Text size="2" weight="medium">Fire only when…</Text>
        <Button type="button" variant="ghost" color="red" onClick={() => onChange(undefined)}>
          <Trash2 size={14} /> Remove filter
        </Button>
      </Flex>
      <FilterNode value={value} onChange={onChange} entityName={entityName} depth={0} />
    </Flex>
  );
}

interface NodeProps {
  value: FilterExpression;
  onChange: (next: FilterExpression) => void;
  onRemove?: () => void;
  entityName: string;
  depth: number;
}

function FilterNode(props: NodeProps) {
  return isComposite(props.value) ? <CompositeNode {...props} /> : <LeafNode {...props} />;
}

function CompositeNode({ value, onChange, onRemove, entityName, depth }: NodeProps) {
  // NOT isn't surfaced in the UI — if a JSON-configured filter brings one in
  // we render its inner expression as a single child of an AND group so users
  // can at least see it. Edits drop the NOT wrap.
  const mode: Mode = value.or ? "or" : "and";
  const children: FilterExpression[] = value.and ?? value.or ?? (value.not ? [value.not] : []);

  function flipMode() {
    onChange(withMode(mode === "and" ? "or" : "and", children));
  }

  function updateChild(idx: number, child: FilterExpression) {
    const next = [...children];
    next[idx] = child;
    onChange(withMode(mode, next));
  }

  function removeChild(idx: number) {
    const next = children.filter((_, i) => i !== idx);
    if (next.length === 0) {
      onRemove?.();
      return;
    }
    onChange(withMode(mode, next));
  }

  function addChild(asGroup: boolean) {
    const child: FilterExpression = asGroup ? { and: [emptyLeaf()] } : emptyLeaf();
    onChange(withMode(mode, [...children, child]));
  }

  return (
    <Card variant={depth === 0 ? "surface" : "classic"} size="2">
      <Flex direction="column" gap="3">
        <Flex align="center" justify="between" gap="2">
          <SegmentedControl.Root
            value={mode}
            onValueChange={(v) => onChange(withMode(v as Mode, children))}
            size="1"
          >
            <SegmentedControl.Item value="and">All of these</SegmentedControl.Item>
            <SegmentedControl.Item value="or">Any of these</SegmentedControl.Item>
          </SegmentedControl.Root>
          {onRemove && depth > 0 && (
            <IconButton
              type="button"
              variant="ghost"
              color="red"
              size="1"
              onClick={onRemove}
              aria-label="Remove group"
            >
              <Trash2 size={14} />
            </IconButton>
          )}
        </Flex>

        <Flex direction="column" gap="2">
          {children.map((child, idx) => (
            <Fragment key={idx}>
              {idx > 0 && <ConnectorChip mode={mode} onFlip={flipMode} />}
              <FilterNode
                value={child}
                onChange={(next) => updateChild(idx, next)}
                onRemove={() => removeChild(idx)}
                entityName={entityName}
                depth={depth + 1}
              />
            </Fragment>
          ))}
        </Flex>

        <Flex gap="2">
          <Button type="button" variant="soft" size="1" onClick={() => addChild(false)}>
            <Plus size={12} /> Condition
          </Button>
          <Button type="button" variant="soft" size="1" onClick={() => addChild(true)}>
            <Plus size={12} /> Group
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
}

function ConnectorChip({ mode, onFlip }: { mode: Mode; onFlip: () => void }) {
  return (
    <Box
      onClick={onFlip}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onFlip(); } }}
      style={{ cursor: "pointer", userSelect: "none", alignSelf: "flex-start" }}
      title="Tap to toggle AND / OR for this group"
    >
      <Badge variant="soft" color={mode === "and" ? "gray" : "orange"} size="2">
        {mode === "and" ? "AND" : "OR"}
      </Badge>
    </Box>
  );
}

function LeafNode({ value, onChange, onRemove, entityName }: NodeProps) {
  const { data: fields = [] } = useQuery({
    queryKey: ["entity-fields", entityName],
    queryFn: () => (entityName ? fieldService.getAllByEntityName(entityName) : Promise.resolve([])),
    enabled: !!entityName,
  });

  const opDef = OPS.find((o) => o.value === value.op) ?? OPS[0];
  const valueStr =
    value.value === undefined || value.value === null
      ? ""
      : Array.isArray(value.value)
        ? value.value.join(",")
        : String(value.value);

  return (
    <Flex align="center" gap="2" wrap="wrap">
      <Select.Root
        value={value.field || ""}
        onValueChange={(v) => onChange({ ...value, field: v })}
      >
        <Select.Trigger placeholder="Field" />
        <Select.Content position="popper" style={DROPDOWN_Z}>
          {fields
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((f) => (
              <Select.Item key={f.id} value={f.name}>
                {f.name}
              </Select.Item>
            ))}
        </Select.Content>
      </Select.Root>

      <Select.Root
        value={value.op || "eq"}
        onValueChange={(v) => onChange({ ...value, op: v as FilterOp })}
      >
        <Select.Trigger />
        <Select.Content position="popper" style={DROPDOWN_Z}>
          {OPS.map((op) => (
            <Select.Item key={op.value} value={op.value}>
              {op.label}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>

      {opDef.hasValue && (
        <TextField.Root
          placeholder={value.op === "in" ? "a,b,c" : "value"}
          value={valueStr}
          onChange={(e) => {
            const raw = e.target.value;
            const parsed = value.op === "in" ? raw.split(",").map((s) => s.trim()) : raw;
            onChange({ ...value, value: parsed });
          }}
          style={{ minWidth: 140 }}
        />
      )}

      {onRemove && (
        <IconButton
          type="button"
          variant="ghost"
          color="red"
          size="1"
          onClick={onRemove}
          aria-label="Remove condition"
        >
          <Trash2 size={14} />
        </IconButton>
      )}
    </Flex>
  );
}
